import {useCallback} from 'react'
import {Trash2} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {Icon} from '@/components/shared/Icon'
import {InlineRow} from '@/components/shared/InlineRow'
import {useI18n} from '@/i18n'
import {toast} from '@/hooks/use-toast'
import {useRevokeApiKey} from '@/features/service-accounts/hooks/useServiceAccounts'

interface RevokeApiKeyDialogProps {
    serviceAccountId: string
    keyId: string | null
    keyName: string
    open: boolean
    onOpenChange: (open: boolean) => void
}

export function RevokeApiKeyDialog({
    serviceAccountId,
    keyId,
    keyName,
    open,
    onOpenChange,
}: RevokeApiKeyDialogProps) {
    const i18n = useI18n()
    const revokeMutation = useRevokeApiKey(serviceAccountId)

    const onConfirm = useCallback(() => {
        if (!keyId) return
        revokeMutation.mutate(keyId, {
            onSuccess: () => {
                toast({description: i18n.apiKeys.revokeDialog.successToast as string})
                onOpenChange(false)
            },
        })
    }, [keyId, revokeMutation, i18n.apiKeys.revokeDialog.successToast, onOpenChange])

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle data-testid="revoke-dialog-title">
                        <InlineRow gap="sm">
                            <Icon icon={Trash2}/>
                            {i18n.apiKeys.revokeDialog.title({name: keyName})}
                        </InlineRow>
                    </DialogTitle>
                    <DialogDescription data-testid="revoke-dialog-body">
                        {i18n.apiKeys.revokeDialog.body}
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        data-testid="btn-cancel-revoke-key"
                    >
                        {i18n.common.cancel}
                    </Button>
                    <Button
                        type="button"
                        variant="destructive"
                        onClick={onConfirm}
                        disabled={revokeMutation.isPending || !keyId}
                        data-testid="btn-confirm-revoke-key"
                    >
                        {i18n.apiKeys.revokeDialog.confirm}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
